import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { getApiError } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { copyTextToClipboard } from "@/lib/clipboard";
import { MARKETPLACE_META } from "@/lib/marketplaces";
import { formatUsedPrice, displayUsedPrice } from "@/lib/price";
import { Copy, Loader2, ExternalLink, Lock, MousePointerClick } from "lucide-react";

export const ListingGenerator = ({ input, strategy, onListing, marketplaces = [] }) => {
  const { t } = useLang();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(null);
  const lastMpRef = useRef(marketplaces[0]);

  const locked = !strategy;

  const generate = useCallback(async (marketplace) => {
    if (!strategy) return;
    lastMpRef.current = marketplace;
    setLoading(marketplace);
    try {
      const res = await api.post("/listing/generate", { input, price: formatUsedPrice(strategy.price), marketplace, goal: strategy.goal });
      setListing(res.data);
      onListing?.(res.data);
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setLoading(null);
    }
  }, [input, strategy, onListing]);

  // Auto-generate the optimised text on load / when the chosen price strategy changes,
  // reusing the last marketplace the user picked (defaults to the first valid one for the category).
  useEffect(() => {
    if (!strategy) { setListing(null); onListing?.(null); return; }
    const mp = marketplaces.includes(lastMpRef.current) ? lastMpRef.current : marketplaces[0];
    generate(mp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategy?.goal, strategy?.price]);

  const copyListing = async () => {
    const ok = await copyTextToClipboard(`${listing.title}\n\n${listing.body}`);
    if (ok) toast.success(t.results.listing_copied);
    else toast.error(t.results.listing_copy_failed);
  };

  const openOnMarketplace = async () => {
    const meta = MARKETPLACE_META[listing.marketplace];
    if (!meta) return;
    await copyTextToClipboard(`${listing.title}\n\n${listing.body}`);
    toast.success(`${t.results.listing_open_hint} ${meta.name}`);
    window.open(meta.url(listing.title), "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="listing-generator" className="lg:col-span-12 border border-black bg-[#F5F5F5] p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <span className="overline text-[#002FA7]">{t.results.listing_title}</span>
          <p className="text-sm text-[#4A4A4A] mt-1 max-w-xl">{t.results.listing_sub}</p>
          {strategy && (
            <div data-testid="listing-selected-price" className="mt-2 inline-flex items-center gap-2 bg-[#002FA7] text-white font-mono text-[10px] font-bold tracking-widest uppercase px-3 py-1.5">
              {t.results.listing_for} {strategy.label} · €{displayUsedPrice(strategy.price)}
            </div>
          )}
        </div>
        <div>
          {!locked && (
            <div data-testid="listing-pick-hint" className="flex items-center gap-2 mb-2 text-[#002FA7]">
              <MousePointerClick size={14} className="animate-pulse flex-shrink-0" />
              <span className="font-mono text-[10px] font-bold tracking-widest uppercase">{t.results.listing_pick_hint}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {marketplaces.map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`listing-btn-${m}`}
                onClick={() => generate(m)}
                disabled={locked || loading !== null}
                className={`font-mono text-xs font-bold tracking-widest uppercase px-4 py-2.5 border border-black transition-colors flex items-center gap-2 ${listing?.marketplace === m ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#FFCC00]/40"} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading === m && <Loader2 size={12} className="animate-spin" />}
                {MARKETPLACE_META[m]?.name || m}
              </button>
            ))}
          </div>
        </div>
      </div>

      {locked && (
        <div data-testid="listing-locked" className="mt-6 border border-dashed border-black/40 bg-white p-5 flex items-center gap-3">
          <Lock size={16} className="text-[#002FA7] flex-shrink-0" />
          <p className="text-sm text-[#4A4A4A]">{t.results.listing_locked}</p>
        </div>
      )}

      {listing && (
        <div data-testid="listing-output" className="mt-6 bg-white border border-black">
          <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-black">
            <span className="overline">{listing.marketplace.toUpperCase()}</span>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid="listing-copy-btn"
                onClick={copyListing}
                className="inline-flex items-center gap-2 font-mono text-xs font-bold tracking-widest uppercase bg-[#FFCC00] px-3 py-2 sm:py-1.5 hover:bg-black hover:text-white transition-colors"
              >
                <Copy size={12} /> {t.results.listing_copy}
              </button>
              <button
                type="button"
                data-testid="listing-open-btn"
                onClick={openOnMarketplace}
                className="inline-flex items-center gap-2 font-mono text-xs font-bold tracking-widest uppercase bg-[#002FA7] text-white px-3 py-2 sm:py-1.5 hover:bg-black transition-colors"
              >
                <ExternalLink size={12} /> {t.results.listing_open} {MARKETPLACE_META[listing.marketplace]?.name}
              </button>
            </div>
          </div>
          <div className="p-5">
            <div data-testid="listing-title" className="font-display font-black text-xl tracking-tight">{listing.title}</div>
            <p data-testid="listing-body" className="mt-3 text-sm text-[#4A4A4A] leading-relaxed whitespace-pre-line">{listing.body}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingGenerator;
